import type { InvestorCommunicationStatus } from "@entrilia/shared/api/investorCommunicationsTypes";
import type { RangeRequestParams } from "@entrilia/shared/api/types";

export const allInvestorCommunicationTypes = ["Article", "Video"] as const;
export type InvestorCommunicationType = (typeof allInvestorCommunicationTypes)[number];

export interface InvestorCommunicationFund {
  id: string;
  name: string;
}

export interface InvestorCommunicationInfo {
  id: string;
  etag: string;
  type: InvestorCommunicationType;
  title?: string;
  fundIds: string[];
  funds: InvestorCommunicationFund[];
  categoryIds: string[];
  status: InvestorCommunicationStatus;
  isReadyForPublishing: boolean;
  createdAt: string;
  createdByUserName: string;
  updatedAt: string;
  updatedByUserName: string;
  publishedAt?: string;
  publishedByUserName?: string;
  publishedToLiveAt?: string;
}

export interface SearchInvestorCommunicationsRequest {
  range: RangeRequestParams;
  types?: InvestorCommunicationType[];
  statuses?: InvestorCommunicationStatus[];
  fundIds?: string[];
  categoryIds?: string[];
  publishedAtFrom?: string;
  publishedAtTo?: string;
  searchTerm?: string;
}

export interface InvestorCommunicationContent {
  title: string;
  description?: string;
}

export interface InvestorCommunicationArticleContent {
  htmlBody: string;
  heroImageFileId?: string;
}

export interface InvestorCommunicationVideoContent {
  videoFileId?: string;
  thumbnailFileId?: string;
  fileName?: string;
  fileSize?: number;
  allowDownload: boolean;
  videoUrl?: string;
  thumbnailUrl?: string;
}

export interface InvestorCommunicationVideoUploadInfo {
  uploadUrl: string;
  fileId: string;
}

export interface InvestorCommunicationBodyImage {
  fileId: string;
  url: string;
}

export interface InvestorCommunication {
  id: string;
  etag: string;
  type: InvestorCommunicationType;
  fundIds: string[];
  funds: InvestorCommunicationFund[];
  categoryIds: string[];
  content?: InvestorCommunicationContent;
  articleContent?: InvestorCommunicationArticleContent;
  videoContent?: InvestorCommunicationVideoContent;
  status: InvestorCommunicationStatus;
  isReadyForPublishing: boolean;
  createdAt: string;
  createdByUserId: string;
  createdByUserName: string;
  updatedAt: string;
  updatedByUserId: string;
  updatedByUserName: string;
  publishedAt?: string;
  publishedByUserId?: string;
  publishedByUserName?: string;
  publishedToLiveAt?: string;
  isPublishedAtCustom: boolean;
}

export interface InvestorCommunicationRecipient {
  investorId: string;
  investorName: string;
  contactId: string;
  contactName: string;
  contactEmail?: string;
}

export interface InvestorCommunicationReader {
  contactId: string;
  viewsCount: number;
  lastViewedAt: string;
  completionPercentage: number;
  totalReadTimeSeconds: number;
}

export interface InvestorCommunicationEngagementSummary {
  recipientsCount: number;
  openedCount: number;
  readFullyCount: number;
  openedNotReadFullyCount: number;
  notOpenedCount: number;
  formerRecipientReadersCount: number;
  avgCompletionPercentage?: number;
  avgReadTimeSeconds?: number;
}

export interface InvestorCommunicationEngagement {
  readers: InvestorCommunicationReader[];
  viewedAt: string[];
  summary: InvestorCommunicationEngagementSummary;
}

export interface InvestorCommunicationContentUpdate {
  titleUserInput: string;
  descriptionUserInput?: string;
}

export interface InvestorCommunicationArticleContentUpdate {
  htmlBodyUserInput: string;
}

export interface InvestorCommunicationVideoContentUpdate {
  allowDownload?: boolean;
}

export interface UpdateInvestorCommunicationRequest {
  expectedEtag: string;
  fundIds?: string[];
  categoryIds?: string[];
  content?: InvestorCommunicationContentUpdate;
  articleContent?: InvestorCommunicationArticleContentUpdate;
  videoContent?: InvestorCommunicationVideoContentUpdate;
  publishedAt?: string;
  isPublishedAtCustom?: boolean;
}

export interface ChangeInvestorCommunicationStatusRequest {
  status: InvestorCommunicationStatus;
  expectedEtag: string;
}
